<template>
  <div class="app-notification" :class="notificationClasses">
    <!-- Icône de type -->
    <div class="app-notification__icon">
      <component :is="iconComponent" />
    </div>

    <!-- Contenu principal -->
    <div class="app-notification__content">
      <div v-if="notification.title" class="app-notification__title">
        {{ notification.title }}
      </div>
      <div class="app-notification__message">
        {{ notification.message }}
      </div>
      
      <!-- Actions -->
      <div v-if="notification.action" class="app-notification__actions">
        <!-- Action simple -->
        <template v-if="notification.action.type !== 'confirm'">
          <button 
            @click.stop="handleAction"
            class="app-notification__action-btn app-notification__action-btn--primary"
          >
            {{ notification.action.label || $t('common.action') }}
          </button>
        </template>
        
        <!-- Actions de confirmation -->
        <template v-else>
          <button 
            @click.stop="handleConfirm"
            class="app-notification__action-btn app-notification__action-btn--confirm"
          >
            {{ $t('common.yes') }}
          </button>
          <button 
            @click.stop="handleCancel"
            class="app-notification__action-btn app-notification__action-btn--cancel"
          >
            {{ $t('common.no') }}
          </button>
        </template>
      </div>
    </div>

    <!-- Bouton de fermeture -->
    <button 
      v-if="notification.closable !== false"
      @click.stop="handleClose"
      class="app-notification__close"
      aria-label="Fermer"
      type="button"
    >
      <i class="fas fa-times"></i>
    </button>

    <!-- Barre de progression (pour les notifications avec durée) -->
    <div 
      v-if="notification.duration > 0"
      class="app-notification__progress"
      :style="progressStyle"
    ></div>
  </div>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
/// <reference types="vue" />
// @ts-ignore
import { computed, ref } from 'vue';
// @ts-ignore
import { onMounted, onUnmounted } from 'vue';

// Composants d'icônes
import CheckCircleIcon from './icons/CheckCircleIcon.vue';
import ExclamationTriangleIcon from './icons/ExclamationTriangleIcon.vue';
import XCircleIcon from './icons/XCircleIcon.vue';
import InformationCircleIcon from './icons/InformationCircleIcon.vue';
import type { ToastNotification, ToastAction, ToastType } from '@/types';

interface Props {
  notification: ToastNotification;
}

interface Emits {
  (e: 'close', notificationId?: number): void;
  (e: 'action', action: ToastAction): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Gestion de la barre de progression
const progress = ref<number>(100);
const progressInterval = ref<NodeJS.Timeout | null>(null);

// Classes CSS dynamiques
const notificationClasses = computed(() => [
  `app-notification--${props.notification.type}`,
  {
    'app-notification--with-title': props.notification.title,
    'app-notification--with-actions': props.notification.action,
    'app-notification--closable': props.notification.closable
  }
]);

// Composant d'icône basé sur le type
const iconComponent = computed(() => {
  const iconMap: Record<string, any> = {
    success: CheckCircleIcon,
    error: XCircleIcon,
    warning: ExclamationTriangleIcon,
    info: InformationCircleIcon
  };
  return iconMap[props.notification.type] || InformationCircleIcon;
});

// Style de la barre de progression
const progressStyle = computed(() => ({
  width: `${progress.value}%`
}));

// Gestionnaires d'événements
const handleAction = (): void => {
  if (props.notification.action?.callback) {
    props.notification.action.callback();
  }
  emit('action', props.notification.action);
  emit('close');
};

const handleConfirm = (): void => {
  if (props.notification.action?.onConfirm) {
    props.notification.action.onConfirm();
  }
  emit('close');
};

const handleCancel = (): void => {
  if (props.notification.action?.onCancel) {
    props.notification.action.onCancel();
  }
  emit('close');
};

const handleClose = (): void => {
  emit('close');
};

// Gestion du timer de progression
const startProgressTimer = (): void => {
  if (props.notification.duration <= 0) return;

  const startTime = Date.now();
  const duration = props.notification.duration;

  progressInterval.value = setInterval(() => {
    const elapsed = Date.now() - startTime;
    const remaining = Math.max(0, duration - elapsed);
    progress.value = (remaining / duration) * 100;

    if (remaining <= 0) {
      if (progressInterval.value) {
        clearInterval(progressInterval.value);
      }
      emit('close');
    }
  }, 50);
};

// Lifecycle
onMounted(() => {
  startProgressTimer();
});

onUnmounted(() => {
  if (progressInterval.value) {
    clearInterval(progressInterval.value);
  }
});
</script>

<style scoped>
.app-notification {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-4);
  background: white;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  border-left: 4px solid;
  min-width: 320px;
  max-width: 480px;
  transition: all var(--transition-base) ease;
  overflow: hidden;
}

.app-notification:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-xl);
}

/* Variantes de types */
.app-notification--success {
  border-left-color: var(--success);
}

.app-notification--error {
  border-left-color: var(--error);
}

.app-notification--warning {
  border-left-color: var(--warning);
}

.app-notification--info {
  border-left-color: var(--info);
}

/* Icône */
.app-notification__icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  margin-top: 2px;
}

.app-notification--success .app-notification__icon {
  color: var(--success);
}

.app-notification--error .app-notification__icon {
  color: var(--error);
}

.app-notification--warning .app-notification__icon {
  color: var(--warning);
}

.app-notification--info .app-notification__icon {
  color: var(--info);
}

/* Contenu */
.app-notification__content {
  flex: 1;
  min-width: 0;
}

.app-notification__title {
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  color: var(--neutral-900);
  margin-bottom: var(--space-1);
  line-height: 1.4;
}

.app-notification__message {
  font-size: var(--font-size-sm);
  color: var(--neutral-700);
  line-height: 1.5;
  word-break: break-word;
}

/* Actions */
.app-notification__actions {
  display: flex;
  gap: var(--space-2);
  margin-top: var(--space-3);
}

.app-notification__action-btn {
  padding: var(--space-1) var(--space-3);
  border-radius: var(--radius-md);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  border: 1px solid transparent;
  cursor: pointer;
  transition: all var(--transition-fast) ease;
}

.app-notification__action-btn--primary {
  background: var(--primary);
  color: white;
  border-color: var(--primary);
}

.app-notification__action-btn--primary:hover {
  background: var(--primary-dark);
  border-color: var(--primary-dark);
}

.app-notification__action-btn--confirm {
  background: var(--success);
  color: white;
  border-color: var(--success);
}

.app-notification__action-btn--confirm:hover {
  background: var(--success-dark);
  border-color: var(--success-dark);
}

.app-notification__action-btn--cancel {
  background: transparent;
  color: var(--neutral-600);
  border-color: var(--neutral-300);
}

.app-notification__action-btn--cancel:hover {
  background: var(--neutral-50);
  color: var(--neutral-700);
  border-color: var(--neutral-400);
}

/* Bouton de fermeture */
.app-notification__close {
  flex-shrink: 0 !important;
  width: 24px !important;
  height: 24px !important;
  background: transparent;
  border: none;
  color: var(--neutral-500);
  cursor: pointer !important;
  border-radius: var(--radius-md);
  transition: all var(--transition-fast) ease;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  pointer-events: auto !important;
  position: relative !important;
  z-index: 999 !important;
}

.app-notification__close:hover {
  background: var(--neutral-100);
  color: var(--neutral-700);
}

/* Barre de progression */
.app-notification__progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: var(--primary);
  transition: width 50ms linear;
  border-radius: 0 0 var(--radius-lg) var(--radius-lg);
}

.app-notification--success .app-notification__progress {
  background: var(--success);
}

.app-notification--error .app-notification__progress {
  background: var(--error);
}

.app-notification--warning .app-notification__progress {
  background: var(--warning);
}

.app-notification--info .app-notification__progress {
  background: var(--info);
}

/* Modifications pour les notifications avec titre */
.app-notification--with-title .app-notification__message {
  font-size: var(--font-size-xs);
}

/* Ajustements pour les notifications sans bouton de fermeture */
.app-notification:not(.app-notification--closable) {
  padding-right: var(--space-4);
}

/* Responsive */
@media (max-width: 640px) {
  .app-notification {
    min-width: 280px;
    max-width: calc(100vw - var(--space-6));
  }
}
</style>
